import { Illustration, IllustrationRecord } from '@loomhq/lens';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  updateShareVideoInUploadMode,
  updateVideoPlayerFlippedCamera,
  updateVideoPlayerPosterUrl,
  updateVideoProcessingError,
  uploadVideoProgress,
} from 'actions/videoPlayer';
import {
  HERMES_ENDPOINT,
  MESSAGE_TYPE_SUBSCRIBE,
  SUBSCRIPTION_TYPE_UPLOAD_PROGRESS,
} from 'constants/hermes';
import { ZOOM_INGESTION } from 'constants/ingestionType';
import { CAM } from 'constants/recordingTypes';
import { LOOM_WS_URI } from 'constants/routes';
import { finishUploadCreator } from 'creators/shareVideo';
import { updateVideoPlayerSourceCreator } from 'creators/videoPlayer';
import { createImage as preloadImage } from 'utilities/image';
import Websocket from 'utilities/websocket';

import { useIosSafariRotatedMedia } from '../../../hooks/useIosSafariRotatedMedia';
import {
  selectUploadProgress,
  selectUploadProgressHook,
} from '../../../selectors/videoPlayer';

import ProgressBar from 'components/share-page-common/progress-bar';
import { EmptyLibrary } from 'components/videos-dashboard-ta/empty-library-ta';

import styles from './styles.module.less';

const LOADING_ANIMATION_COMPLETE_TIMEOUT = 1800;

const waitForLoadingAnimation = () =>
  new Promise(resolve =>
    setTimeout(resolve, LOADING_ANIMATION_COMPLETE_TIMEOUT)
  );

const useLoadingProgressHook = setIsComplete => {
  const dispatch = useDispatch();
  const webSocketRef = useRef();
  const progressValueRef = useRef(0);
  const uploadModeRef = useRef();

  const { videoId, complete, sourceUrlIsTranscoded, isFetchingVideoSrc } =
    useSelector(selectUploadProgressHook);

  const { isIosRotatedMedia } = useIosSafariRotatedMedia();

  const onWebSocketReady = useCallback(() => {
    const url = window.location.pathname.split('/');
    const videoId = url.pop();

    webSocketRef.current.send(
      JSON.stringify({
        type: MESSAGE_TYPE_SUBSCRIBE,
        subscription_data: {
          type: SUBSCRIPTION_TYPE_UPLOAD_PROGRESS,
          video_id: videoId,
          listenForTrancodedVideo: isIosRotatedMedia,
        },
      })
    );
  }, [isIosRotatedMedia]);

  const exitUploadMode = useCallback(() => {
    dispatch(updateShareVideoInUploadMode(false));
    dispatch(finishUploadCreator(videoId));
  }, [dispatch, videoId]);

  const onUploadDone = useCallback(async () => {
    setIsComplete(true);

    await waitForLoadingAnimation();

    exitUploadMode();

    webSocketRef.current.close();
  }, [exitUploadMode, setIsComplete]);

  const onWebSocketError = useCallback(
    err => {
      // exit uploadmode, fallback
      exitUploadMode();
      console.error('ws error', err);
    },
    [exitUploadMode]
  );

  const onProgress = useCallback(
    progress => {
      // make sure progress is between 0 and 100
      // and does not go back
      const latestProgress = Math.max(progressValueRef.current, progress);
      let progressCapped = Math.min(latestProgress, 100);

      if (isIosRotatedMedia) {
        progressCapped = 80;
      }

      progressValueRef.current = progressCapped;

      dispatch(uploadVideoProgress(progressCapped));
    },
    [dispatch, isIosRotatedMedia]
  );

  const onWebSocketMessage = useCallback(
    ({ data }) => {
      const message = JSON.parse(data);

      if (!message) {
        return;
      }

      if (message.videoUploadValid === false) {
        dispatch(
          updateVideoProcessingError(
            message.videoUploadValid,
            message.videoUploadMessage
          )
        );
      }

      if (message.isFlippedThumb) {
        dispatch(updateVideoPlayerFlippedCamera(message.isFlippedThumb));
      }

      if (message.thumbUrl) {
        // preload thumb so we have a smooth experience
        // instead of seeing the image progressively loading
        // on slow connections
        preloadImage(message.thumbUrl)
          .then(img => img.src)
          .then(url => {
            dispatch(updateVideoPlayerPosterUrl(url));
          })
          // edge case, we don't care
          .catch(console.error);
      }

      // handle case video already uploaded
      // no waiting time, no animation, just exit
      if (message.noUpload === true && !isIosRotatedMedia) {
        exitUploadMode();

        return webSocketRef.current.close();
      }

      onProgress(message.progress);

      if (message.done === true && !isIosRotatedMedia) {
        return onUploadDone();
      }

      if (isIosRotatedMedia && message.isTranscoded === true) {
        dispatch(
          updateVideoPlayerSourceCreator({
            forceMp4: true,
          })
        );

        return onUploadDone();
      }

      if (!uploadModeRef.current) {
        dispatch(updateShareVideoInUploadMode(true));
      }
    },
    [dispatch, exitUploadMode, isIosRotatedMedia, onProgress, onUploadDone]
  );

  useEffect(() => {
    // If no video ID or if we've already turned in uploadMode
    // do not restart hook
    if (
      !videoId ||
      isFetchingVideoSrc ||
      (!isIosRotatedMedia && complete) ||
      (isIosRotatedMedia && sourceUrlIsTranscoded)
    ) {
      return;
    }

    console.log('Starting progress bar subscription');

    webSocketRef.current = new Websocket(LOOM_WS_URI + HERMES_ENDPOINT);

    webSocketRef.current.ready(onWebSocketReady);
    webSocketRef.current.onMessage(onWebSocketMessage);
    webSocketRef.current.onError(onWebSocketError);
    webSocketRef.current.onClose(() =>
      console.log('Closing progress bar subscription')
    );

    return () => {
      console.log('Unmounting progress subscription hook');
      webSocketRef.current.close();
    };
  }, [
    complete,
    dispatch,
    isFetchingVideoSrc,
    isIosRotatedMedia,
    onWebSocketError,
    onWebSocketMessage,
    onWebSocketReady,
    sourceUrlIsTranscoded,
    videoId,
  ]);
};

const UploadProgress = () => {
  const [isComplete, setIsComplete] = useState(false);

  const {
    inUploadMode,
    screenType,
    flippedCamera,
    posterUrl,
    videoUploadProgress,
    complete,
    videoUploadValid,
    ingestionType,
  } = useSelector(selectUploadProgress);

  const isFlipped = screenType === CAM && flippedCamera;
  const hasProcessingErr = videoUploadValid === false;

  useLoadingProgressHook(setIsComplete);

  const { isIosRotatedMedia, sourceUrlIsTranscoded } =
    useIosSafariRotatedMedia();

  if ((complete && !isIosRotatedMedia) || hasProcessingErr) {
    return null;
  }

  if (isIosRotatedMedia && sourceUrlIsTranscoded) {
    return null;
  }

  if (!complete && ingestionType === ZOOM_INGESTION) {
    return (
      <div className={styles.ingestionInProgress}>
        <EmptyLibrary
          centerpiece={
            <Illustration color="grey4" illustration={<IllustrationRecord />} />
          }
          description="You will be notified when the upload completes,
            and can share this page now or when your video is ready."
          title="Upload in progress"
          spacerTop={10}
          spacerBottom={11}
          backgroundColor={'grey1'}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        styles.uploadProgress,
        isComplete ? styles.isCompleted : '',
        inUploadMode ? styles.active : ''
      )}
    >
      <div className={styles.loadingThumbnailContainer}>
        <div
          className={classNames(
            styles.loadingThumbnailImage,
            isFlipped ? styles.flipped : ''
          )}
          style={posterUrl && { backgroundImage: `url(${posterUrl})` }}
        />
      </div>
      <ProgressBar progress={videoUploadProgress} isComplete={isComplete} />
    </div>
  );
};

export default UploadProgress;
